import type { KeyboardEvent, ReactElement } from "react";
import { EllipsisVertical, Pin, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverMenuItem,
  PopoverSeparator,
  PopoverTrigger,
} from "../molecules/index.js";
import {
  ACTIVE_GLOW,
  ACTIVE_ICON,
  ACTIVE_INDICATOR,
  NEUTRAL_ACTIVE_ICON,
  NEUTRAL_ACTIVE_INDICATOR,
} from "./tab-accents.js";
import { TabBaseFlare } from "./TabBaseFlare.js";
import type { Tab } from "./tabs.types.js";

/**
 * 24×24 hit areas (WCAG 2.5.8) for the two secondary controls: invisible on
 * an inactive tab until hover or focus-within, by opacity so they stay in the
 * Tab order and appear as a keyboard user reaches them.
 */
const CONTROL_BOX =
  "flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-pb-text-muted transition-opacity hover:text-pb-text hover:pb-overlay-paint focus-ring";
const REVEAL_ON_INTERACTION =
  "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100";

export interface TabStripItemProps {
  readonly tab: Tab;
  readonly isActive: boolean;
  /** Roving tabindex anchor: `0` on the focused route, `-1` elsewhere. */
  readonly tabIndex: 0 | -1;
  readonly buttonRef: (node: HTMLButtonElement | null) => void;
  readonly onSelect: () => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  readonly canClose: boolean;
  readonly canCloseOthers: boolean;
  readonly onClose: ((id: string) => void) | undefined;
  readonly onCloseOthers: ((id: string) => void) | undefined;
  readonly onTogglePin: ((id: string) => void) | undefined;
  /** Chrome-style divider at the start edge (d14): every tab but the first and the one after the active tab; skipped while active. */
  readonly dividerBefore: boolean;
}

/**
 * One browser-style tab (PB-5176, Direction A): a 40px rounded-top shape on
 * the strip's baseline. The active tab carries the elevated fill, a three-side
 * border, concave base flares (d11) and the module accent underline; inactive
 * tabs are borderless. Rendered only by `TabStrip`, which owns roving focus.
 * An action the host gave no callback for renders disabled, and the close
 * button not at all, so nothing is offered that would do nothing.
 *
 * The container carries the full-label `title` (PB-4744): the label truncates,
 * so pointer users need the reveal, and on the non-interactive container it
 * adds no accessible name — on the button it would be a duplicate description.
 * Close and Tab actions carry their own `title`.
 */
export function TabStripItem({
  tab,
  isActive,
  tabIndex,
  buttonRef,
  onSelect,
  onKeyDown,
  canClose,
  canCloseOthers,
  onClose,
  onCloseOthers,
  onTogglePin,
  dividerBefore,
}: TabStripItemProps): ReactElement {
  const { icon: Icon, accent } = tab;
  const activeIcon = (accent && ACTIVE_ICON[accent]) || NEUTRAL_ACTIVE_ICON;
  const activeIndicator =
    (accent && ACTIVE_INDICATOR[accent]) || NEUTRAL_ACTIVE_INDICATOR;
  const activeGlow = (accent && ACTIVE_GLOW[accent]) || "";
  const fullLabel = `${tab.module ? `${tab.module} / ` : ""}${tab.label}`;
  const reveal = isActive ? "opacity-100" : REVEAL_ON_INTERACTION;
  const closable = canClose && onClose !== undefined;

  return (
    <div
      title={fullLabel}
      data-tab-shape=""
      // 160–232px wide (the artboard's 158–230 on the 4px scale) and 40px tall
      // so 8px of bar shows above the tab (d11); `pb-0.5` centres the row above
      // the 2px underline. The active tab paints above its neighbours' flares.
      className={`group relative flex h-10 min-w-40 max-w-58 shrink-0 items-center gap-1 rounded-t-md border border-b-0 ps-2.5 pe-1 pb-0.5 transition-colors ${
        isActive
          ? "z-10 border-pb-border bg-pb-card-elevated text-pb-text"
          : "border-transparent text-pb-text-muted after:pointer-events-none after:absolute after:inset-0 after:rounded-t-md after:opacity-0 after:transition-opacity after:duration-motion-fast after:content-[''] after:pb-overlay-paint hover:border-pb-border/70 hover:pb-overlay-paint hover:text-pb-text-secondary hover:after:opacity-100 motion-reduce:after:transition-none"
      }`}
    >
      {dividerBefore && !isActive ? (
        // In the 2px gap before this tab, 16px tall on the row's centre line;
        // faded while this tab or the one before it is hovered (double edge).
        <span
          aria-hidden
          data-tab-divider=""
          className="pointer-events-none absolute top-0 bottom-0.5 -start-0.5 my-auto h-4 w-px bg-pb-border-strong transition-opacity group-hover:opacity-0 [[data-tab-shape]:hover+[data-tab-shape]>&]:opacity-0"
        />
      ) : null}
      {isActive ? (
        <>
          <TabBaseFlare side="start" />
          <TabBaseFlare side="end" />
          <span
            aria-hidden
            className={`pointer-events-none absolute inset-x-2 bottom-0 h-0.5 rounded-t-full ${activeIndicator} ${activeGlow}`}
          />
        </>
      ) : null}
      <button
        ref={buttonRef}
        type="button"
        aria-current={isActive ? "page" : undefined}
        tabIndex={tabIndex}
        onClick={onSelect}
        onKeyDown={onKeyDown}
        className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-sm py-1 focus-ring"
      >
        {tab.pinned ? (
          <Pin className="h-4 w-4 shrink-0 text-pb-gold-text opacity-70" />
        ) : Icon ? (
          <span className="relative flex shrink-0">
            {/* `flex`: no baseline gap below the inline SVG, so it sits on the centre line. */}
            <Icon
              className={`h-4 w-4 ${
                isActive
                  ? activeIcon
                  : "text-pb-text-muted group-hover:text-pb-text-secondary"
              }`}
            />
            {tab.dotColor ? (
              <span
                className={`absolute -top-0.5 -end-0.5 h-1.5 w-1.5 rounded-full ${tab.dotColor}`}
              />
            ) : null}
          </span>
        ) : null}
        <span
          className={`truncate text-body-small ${isActive ? "font-semibold" : "font-medium"}`}
        >
          {fullLabel}
        </span>
      </button>
      <Popover align="start">
        <PopoverTrigger>
          <button
            type="button"
            aria-label={`Tab actions for ${tab.label}`}
            title="Tab actions"
            className={`${CONTROL_BOX} ${tab.pinned ? "opacity-70" : reveal} aria-expanded:opacity-100`}
          >
            <EllipsisVertical className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-max" sideOffset={8}>
          <PopoverMenuItem
            icon={Pin}
            disabled={!onTogglePin}
            onClick={() => onTogglePin?.(tab.id)}
          >
            {tab.pinned ? "Unpin tab" : "Pin this tab"}
          </PopoverMenuItem>
          <PopoverSeparator />
          <PopoverMenuItem
            disabled={!closable}
            onClick={() => {
              if (closable) onClose?.(tab.id);
            }}
          >
            Close tab
          </PopoverMenuItem>
          <PopoverMenuItem
            disabled={!canCloseOthers || !onCloseOthers}
            onClick={() => {
              if (canCloseOthers) onCloseOthers?.(tab.id);
            }}
          >
            Close other tabs
          </PopoverMenuItem>
        </PopoverContent>
      </Popover>
      {closable ? (
        <button
          type="button"
          aria-label={`Close ${tab.label}`}
          title="Close tab"
          onClick={() => onClose?.(tab.id)}
          className={`${CONTROL_BOX} ${reveal}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}
